import { useRef } from 'react'
import { useDebounce } from 'react-use'
import { isEqual } from 'lodash'

/**
 * Debounces a filter change, without emitting a value the user has not changed.
 *
 * `useDebounce` from react-use also runs its callback on the first render. For a filter component
 * whose debounced state is initialised from its props, that means re-emitting the value it has just
 * received — a write that changes nothing, but still adds a history entry (the browser back button
 * then needs as many clicks as there were mounts to leave the page). Worse, if the component
 * remounts while a newer change is in flight, that re-emitted value is stale and overwrites it.
 *
 * Skipping the *first run* would not do: `useDebounce` reschedules its timer whenever the value
 * changes, so typing within the delay of the mount cancels the mount run and the first real search
 * would be the one swallowed. We compare with the value received at mount instead — and once a real
 * change has been emitted, everything is emitted, so that clearing a filter back to its initial
 * value still goes through.
 */
export const useDebouncedFilters = <T>(
  filters: T,
  onFiltersChange: (filters: T) => void,
  delay = 500,
) => {
  const initialFilters = useRef(filters)
  const hasEmitted = useRef(false)

  useDebounce(
    () => {
      if (!hasEmitted.current && isEqual(filters, initialFilters.current)) {
        return
      }
      hasEmitted.current = true
      onFiltersChange(filters)
    },
    delay,
    [filters],
  )
}
